import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { ShieldCheck, X, CheckCircle } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchMyPendingPolicies,
  readPolicyVersion,
  acknowledgePolicy,
  type MyPendingPolicy,
} from '@/lib/policiesApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

export default function MyPoliciesPage() {
  const navigate = useNavigate();
  const [reading, setReading] = useState<MyPendingPolicy | null>(null);
  const q = useQuery({ queryKey: ['my-pending-policies'], queryFn: fetchMyPendingPolicies });
  const pending = q.data ?? [];

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="My Policies"
        subtitle="Chambers policies awaiting your acceptance."
      />

      {q.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <CheckCircle className="h-8 w-8 text-emerald-500" />
            <div className="text-sm font-medium text-foreground">You're all caught up</div>
            <div className="text-xs text-muted-foreground">No policies need your acceptance right now.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pending.map((p) => (
            <Card key={p.versionId}>
              <CardContent className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                    <span className="truncate font-medium text-foreground">{p.title}</span>
                  </div>
                  {p.category && <div className="mt-0.5 text-xs text-muted-foreground">{p.category}</div>}
                </div>
                <Button variant="primary" size="sm" onClick={() => setReading(p)}>Read & accept</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {reading && (
        <ReadAcceptModal
          versionId={reading.versionId}
          onClose={() => setReading(null)}
          onDone={() => { setReading(null); q.refetch(); }}
        />
      )}
    </div>
  );
}

function ReadAcceptModal({ versionId, onClose, onDone }: { versionId: string; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['policy-version', versionId], queryFn: () => readPolicyVersion(versionId) });
  const v = q.data;

  const mut = useMutation({
    mutationFn: (accept: boolean) => acknowledgePolicy(versionId, accept),
    onSuccess: (_r, accept) => {
      toast.success(accept ? 'Policy accepted' : 'Response recorded');
      qc.invalidateQueries({ queryKey: ['my-policy-count'] });
      onDone();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not record your response')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center" onClick={onClose}>
      <ScrollLock />
      <div className="my-8 flex w-full max-w-2xl flex-col rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <span className="text-sm font-semibold text-foreground">{v?.title ?? 'Policy'}</span>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
          {!v ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div
              className="prose prose-sm max-w-none text-sm text-foreground dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(v.bodyHtml || '<p>No content.</p>') }}
            />
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <p className="text-xs text-muted-foreground">By accepting you confirm you have read and agree to this policy.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => mut.mutate(false)} disabled={mut.isPending}>Decline</Button>
            <Button variant="primary" size="sm" onClick={() => mut.mutate(true)} disabled={mut.isPending || !v}>
              {mut.isPending ? 'Saving…' : 'Accept'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
